/**
 * useStoriesManifest — fetches the published-stories manifest emitted by the
 * studysync-stories Astro repo (plan §6 / Task 4) and shares it via a module-level cache.
 *
 * Source: `${REACT_APP_STORIES_URL}stories.json` (the Astro route src/pages/stories.json.ts).
 * The manifest is split by the `type` field into success-stories vs ongoing-projects
 * downstream (plan §4.8 / §4.9).
 *
 * Resilience contract (Task 5 acceptance): on ANY failure — network error, non-2xx (e.g.
 * a 404 when stories aren't deployed yet), or malformed JSON — this returns an empty array
 * and NEVER throws to the UI.
 */

import { useEffect, useState } from 'react';

/** One published story, mirroring the manifest emitted by stories.json.ts. */
export interface StoryManifestEntry {
  slug: string;
  title: string;
  lab: string;
  date: string; // YYYY-MM-DD
  summary: string;
  ogImage: string | null;
  type: string; // 'success-story' | 'ongoing-project'
  status: string | null;
}

// In production the stories site is VENDORED into the web app at `public/stories/`
// (via `npm run export` in the studysync-stories repo), so the manifest is served
// SAME-ORIGIN at `/stories/stories.json` on every deploy (prod + preview). Hence the
// default is the relative `/stories/` — no env var needed in any deployed environment.
// REACT_APP_STORIES_URL is an OPTIONAL override (include a trailing slash), used in
// local dev to point at the live Astro dev server (e.g. "http://localhost:4321/stories/").
const STORIES_BASE = process.env.REACT_APP_STORIES_URL || '/stories/';
const MANIFEST_URL = `${STORIES_BASE}stories.json`;

export interface UseStoriesManifestResult {
  stories: StoryManifestEntry[];
  loading: boolean;
}

// Module-level cache so the manifest is fetched at most once per page load.
let cachedStories: StoryManifestEntry[] | null = null;
let inFlight: Promise<StoryManifestEntry[]> | null = null;

function fetchManifest(): Promise<StoryManifestEntry[]> {
  if (cachedStories) return Promise.resolve(cachedStories);
  if (inFlight) return inFlight;

  inFlight = fetch(MANIFEST_URL, { headers: { Accept: 'application/json' } })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`stories.json responded ${res.status}`);
      }
      return res.json();
    })
    .then((json: unknown) => {
      const list = Array.isArray(json) ? (json as StoryManifestEntry[]) : [];
      cachedStories = list;
      return list;
    })
    .catch(() => {
      // Never surface an error to the UI — an absent/failed manifest just means no stories.
      cachedStories = [];
      return [] as StoryManifestEntry[];
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function useStoriesManifest(): UseStoriesManifestResult {
  const [stories, setStories] = useState<StoryManifestEntry[]>(cachedStories || []);
  const [loading, setLoading] = useState<boolean>(cachedStories === null);

  useEffect(() => {
    let cancelled = false;

    if (cachedStories) {
      setStories(cachedStories);
      setLoading(false);
      return;
    }

    fetchManifest()
      .then((list) => {
        if (!cancelled) setStories(list);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { stories, loading };
}

// Test/dev helper: clear the module-level cache so a fresh fetch occurs.
export function _resetStoriesManifestCache(): void {
  cachedStories = null;
  inFlight = null;
}

export default useStoriesManifest;
